import React from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { AlertTriangle, Clock, MapPin, Calendar, Ban, Eye } from "lucide-react";
import { formatDateFr } from "../lib/dateFormat";

/**
 * Modale BLOQUANTE de confirmation de conflit horaire / indisponibilité.
 *
 * Props :
 *  - open : boolean
 *  - onOpenChange(open) : callback
 *  - conflict : { shifts: [...], leaves: [...], conflict_dates?: [...], message? }
 *  - onCancel() : utilisateur clique sur "Annuler" (refus du bypass)
 *  - onConfirm() : utilisateur clique sur "Confirmer quand même" (bypass force=true)
 *  - busy : bool — désactive les boutons pendant le re-submit
 *  - onViewShift(shift) : (optionnel) ouvre la fiche du shift existant signalé
 */
export default function ShiftConflictDialog({
  open, onOpenChange, conflict, onCancel, onConfirm, busy = false, onViewShift,
}) {
  const shifts = conflict?.shifts || [];
  const leaves = conflict?.leaves || [];
  const conflictDates = conflict?.conflict_dates || [];
  const total = shifts.length + leaves.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl" data-testid="shift-conflict-dialog">
        <DialogHeader>
          <DialogTitle className="inline-flex items-center gap-2 text-amber-700">
            <AlertTriangle size={20} className="text-amber-600" />
            Conflit de planification détecté
          </DialogTitle>
          <DialogDescription>
            {total > 0 ? (
              <>
                <span className="font-medium text-zinc-900">
                  {total} conflit{total > 1 ? "s" : ""} trouvé{total > 1 ? "s" : ""}
                </span>{" "}
                pour ce collaborateur. Vérifiez les détails ci-dessous avant de confirmer.
              </>
            ) : (
              "Conflit détecté."
            )}
          </DialogDescription>
        </DialogHeader>

        <div
          className="space-y-3 max-h-[55vh] overflow-y-auto pr-1"
          data-testid="shift-conflict-list"
        >
          {shifts.length > 0 && (
            <div className="space-y-2">
              <p className="label-uppercase text-xs text-zinc-700">
                Shift{shifts.length > 1 ? "s" : ""} existant{shifts.length > 1 ? "s" : ""}
              </p>
              {shifts.map((s, idx) => (
                <div
                  key={s.id || idx}
                  className="border border-amber-200 bg-amber-50 rounded-sm p-3 text-sm"
                  data-testid={`conflict-shift-${idx}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 text-zinc-900 font-medium">
                        <Calendar size={14} className="text-amber-700" />
                        {formatDateFr(s.date)}
                        {s.duplicate_date && s.duplicate_date !== s.date && (
                          <span className="text-[11px] font-mono text-amber-700 ml-1">
                            (duplication vers {formatDateFr(s.duplicate_date)})
                          </span>
                        )}
                      </div>
                      <div className="mt-1 flex items-center gap-2 text-zinc-700">
                        <Clock size={13} className="text-zinc-500" />
                        <span className="font-mono">{s.start_time} → {s.end_time}</span>
                      </div>
                      <div className="mt-1 flex items-center gap-2 text-zinc-700">
                        <MapPin size={13} className="text-zinc-500" />
                        <span>
                          {s.brand_name || "—"}
                          {s.site_name ? ` · ${s.site_name}` : ""}
                          {s.shift_category_name ? ` · ${s.shift_category_name}` : ""}
                        </span>
                      </div>
                      {s.role_label && (
                        <p className="mt-1 text-[11px] text-zinc-500 font-mono">
                          Poste : {s.role_label}
                        </p>
                      )}
                    </div>
                    {onViewShift && s.id && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-amber-800 hover:bg-amber-100 hover:text-amber-900 shrink-0"
                        onClick={() => onViewShift(s)}
                        data-testid={`conflict-shift-view-${idx}`}
                        title="Voir la fiche complète du shift en conflit"
                      >
                        <Eye size={13} className="mr-1" />
                        Voir la fiche
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {leaves.length > 0 && (
            <div className="space-y-2">
              <p className="label-uppercase text-xs text-zinc-700">
                Indisponibilité{leaves.length > 1 ? "s" : ""} / congé{leaves.length > 1 ? "s" : ""}
              </p>
              {leaves.map((lv, idx) => (
                <div
                  key={lv.id || idx}
                  className="border border-rose-200 bg-rose-50 rounded-sm p-3 text-sm"
                  data-testid={`conflict-leave-${idx}`}
                >
                  <div className="flex items-center gap-2 text-zinc-900 font-medium">
                    <Ban size={14} className="text-rose-700" />
                    {formatDateFr(lv.start_date)} → {formatDateFr(lv.end_date)}
                  </div>
                  <div className="mt-1 text-zinc-700">
                    <span className="font-mono text-[11px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-rose-100 text-rose-800">
                      {lv.status === "approuve" ? "Approuvée" : lv.status === "en_attente" ? "En attente" : lv.status}
                    </span>
                    {lv.category_name && (
                      <span className="ml-2 text-zinc-700">{lv.category_name}</span>
                    )}
                  </div>
                  {lv.reason && (
                    <p className="mt-1 text-[11px] text-zinc-600 italic">« {lv.reason} »</p>
                  )}
                </div>
              ))}
            </div>
          )}

          {conflictDates.length > 0 && (
            <p className="text-[11px] text-zinc-500 font-mono pt-2 border-t border-zinc-100">
              Dates concernées : {conflictDates.map(formatDateFr).join(" · ")}
            </p>
          )}
        </div>

        <DialogFooter className="gap-2 mt-2">
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={busy}
            data-testid="shift-conflict-cancel-btn"
          >
            Annuler
          </Button>
          <Button
            onClick={onConfirm}
            disabled={busy}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="shift-conflict-confirm-btn"
          >
            {busy ? "…" : "Confirmer quand même"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
